import { Box } from '@chakra-ui/react';
import { StatusPieChart } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CommonTreeCell/StatusPieChart';

import { Divider } from 'uiToolkitV1/Divider';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import { EMPTY_VALUE_ENGAGEMENT_SURVEY_GRID } from '../EngagementSurveyReportsGrid';

// Types for row data and custom tooltip parameters
interface RowData {}

interface CustomTooltipParams {
  value: unknown;
  data: RowData;
  colDef: {
    field?: string;
    headerName?: string;
  };
}

const EMPLOYEES_CONFIG_NPS = {
  DETRACTOR_EMPLOYEES: {
    name: 'Detractor employees',
    color: '#C62C02',
  },
  PASSIVE_EMPLOYEES: {
    name: 'Passive employees',
    color: '#FD812D',
  },
  PROMOTER_EMPLOYEES: {
    name: 'Promoter employees',
    color: '#2F8133',
  },
};

const EMPLOYEES_CONFIG_ENGAGEMENT = {
  LOW_SCORE_EMPLOYEES: {
    name: 'Employees with 0-24 score ',
    color: '#C62C02',
  },
  MEDIUM_SCORE_EMPLOYEES: {
    name: 'Employees with 25-49 score',
    color: '#FD812D',
  },
  HIGH_SCORE_EMPLOYEES: {
    name: 'Employees with 50-74 score',
    color: '#44812F',
  },
  TOP_SCORE_EMPLOYEES: {
    name: 'Employees with 75+ score ',
    color: '#1A5724',
  },
};

const EmployeesCountListItem = ({ color, count, label }) => (
  <Box display="flex" padding="2px 4px" flexDirection="column" alignItems="flex-start" gap="4px">
    <Box display="flex" height="24px" padding="8px 0px" alignItems="center" gap="4px" alignSelf="stretch">
      <TextLabel color={color}>{label}</TextLabel>
    </Box>
    <TextBodyMedium>{count}</TextBodyMedium>
  </Box>
);

function transformObject(obj: Record<string, unknown>): Record<string, unknown> {
  const newObj: Record<string, unknown> = {};

  Object.keys(obj).forEach((key) => {
    const newKey = key.includes('__') ? key.split('__')[1] : key;
    newObj[newKey] = obj[key];
  });

  return newObj;
}

export const SurveyGridModal: React.FC<CustomTooltipParams> = (props) => {
  // The props contain information about the cell being hovered
  const { data } = props;
  const cellValuesMap: Record<string, unknown> = transformObject(data);

  const totalEmployeesCount = cellValuesMap.EMPLOYEE_COUNT ?? EMPTY_VALUE_ENGAGEMENT_SURVEY_GRID;
  const lowScoreEmployeesCount = cellValuesMap.COUNT_0_24 ?? EMPTY_VALUE_ENGAGEMENT_SURVEY_GRID;
  const mediumScoreEmployeesCount = cellValuesMap.COUNT_25_49 ?? EMPTY_VALUE_ENGAGEMENT_SURVEY_GRID;
  const highScoreEmployeesCount = cellValuesMap.COUNT_50_74 ?? EMPTY_VALUE_ENGAGEMENT_SURVEY_GRID;
  const topScoreEmployeesCount = cellValuesMap.COUNT_75_PLUS ?? EMPTY_VALUE_ENGAGEMENT_SURVEY_GRID;

  const scoreType = cellValuesMap.SCORE_TYPE ?? EMPTY_VALUE_ENGAGEMENT_SURVEY_GRID;
  const score = cellValuesMap.SCORE ?? EMPTY_VALUE_ENGAGEMENT_SURVEY_GRID;
  const isEngagementScoreType = scoreType === 'ENGAGEMENT_SCORE';

  const chartDataEngagement = Object.values(EMPLOYEES_CONFIG_ENGAGEMENT).map(({ name, color }) => {
    if (name === EMPLOYEES_CONFIG_ENGAGEMENT.LOW_SCORE_EMPLOYEES.name) {
      return {
        name,
        color,
        value: lowScoreEmployeesCount,
      };
    }
    if (name === EMPLOYEES_CONFIG_ENGAGEMENT.MEDIUM_SCORE_EMPLOYEES.name) {
      return {
        name,
        color,
        value: mediumScoreEmployeesCount,
      };
    }
    if (name === EMPLOYEES_CONFIG_ENGAGEMENT.HIGH_SCORE_EMPLOYEES.name) {
      return {
        name,
        color,
        value: highScoreEmployeesCount,
      };
    }
    return {
      name,
      color,
      value: topScoreEmployeesCount,
    };
  });

  const detractorEmployeesCount = cellValuesMap.DETRACTOR_COUNT ?? EMPTY_VALUE_ENGAGEMENT_SURVEY_GRID;
  const passiveEmployeesCount = cellValuesMap.NEUTRAL_COUNT ?? EMPTY_VALUE_ENGAGEMENT_SURVEY_GRID;
  const promoterEmployeesCount = cellValuesMap.PROMOTER_COUNT ?? EMPTY_VALUE_ENGAGEMENT_SURVEY_GRID;

  if (isEngagementScoreType) {
    return (
      <Box
        display="inline-flex"
        padding="12px"
        flexDirection="column"
        alignItems="flex-start"
        gap="8px"
        borderRadius="8px"
        background="#fff"
        boxShadow="0px 0px 2px 0px rgba(0, 0, 0, 0.25)"
        width="248px"
      >
        <TextLabel bold padding="4px 0px">
          Distribution
        </TextLabel>

        <Box display="flex" alignItems="center" gap="8px" width="100%">
          <Box display="flex" flexDirection="column" alignItems="flex-start" gap="8px" flex="1">
            <TextLabel color="#666">Total employees</TextLabel>
            <TextLabel bold fontSize="24px" lineHeight="135%">
              {totalEmployeesCount}
            </TextLabel>
          </Box>

          <Box h="72px" w="72px" cursor="pointer">
            <StatusPieChart data={chartDataEngagement} innerRadius={22} outerRadius={36} />
          </Box>
        </Box>

        <EmployeesCountListItem
          color={EMPLOYEES_CONFIG_ENGAGEMENT.LOW_SCORE_EMPLOYEES.color}
          count={detractorEmployeesCount}
          label={EMPLOYEES_CONFIG_ENGAGEMENT.LOW_SCORE_EMPLOYEES.name}
        />
        <EmployeesCountListItem
          color={EMPLOYEES_CONFIG_ENGAGEMENT.MEDIUM_SCORE_EMPLOYEES.color}
          count={passiveEmployeesCount}
          label={EMPLOYEES_CONFIG_ENGAGEMENT.MEDIUM_SCORE_EMPLOYEES.name}
        />
        <EmployeesCountListItem
          color={EMPLOYEES_CONFIG_ENGAGEMENT.HIGH_SCORE_EMPLOYEES.color}
          count={promoterEmployeesCount}
          label={EMPLOYEES_CONFIG_ENGAGEMENT.HIGH_SCORE_EMPLOYEES.name}
        />
        <EmployeesCountListItem
          color={EMPLOYEES_CONFIG_ENGAGEMENT.TOP_SCORE_EMPLOYEES.color}
          count={promoterEmployeesCount}
          label={EMPLOYEES_CONFIG_ENGAGEMENT.TOP_SCORE_EMPLOYEES.name}
        />
        <Divider opacity={1} borderColor="#CFD7DF" marginLeft="auto" marginRight="auto" width="95%" />

        <TextLabel padding="4px 0px">{`Score: ${score}`}</TextLabel>
      </Box>
    );
  }

  const chartDataNPS = Object.values(EMPLOYEES_CONFIG_NPS).map(({ name, color }) => {
    if (name === EMPLOYEES_CONFIG_NPS.DETRACTOR_EMPLOYEES.name) {
      return {
        name,
        color,
        value: detractorEmployeesCount,
      };
    }
    if (name === EMPLOYEES_CONFIG_NPS.PASSIVE_EMPLOYEES.name) {
      return {
        name,
        color,
        value: passiveEmployeesCount,
      };
    }
    return {
      name,
      color,
      value: promoterEmployeesCount,
    };
  });

  return (
    <Box
      display="inline-flex"
      padding="12px"
      flexDirection="column"
      alignItems="flex-start"
      gap="8px"
      borderRadius="8px"
      background="#fff"
      boxShadow="0px 0px 2px 0px rgba(0, 0, 0, 0.25)"
      width="248px"
    >
      <TextLabel bold padding="4px 0px">
        Distribution
      </TextLabel>

      <Box display="flex" alignItems="center" gap="8px" width="100%">
        <Box display="flex" flexDirection="column" alignItems="flex-start" gap="8px" flex="1">
          <TextLabel color="#666">Total employees</TextLabel>
          <TextLabel bold fontSize="24px" lineHeight="135%">
            {totalEmployeesCount}
          </TextLabel>
        </Box>

        <Box h="72px" w="72px" cursor="pointer">
          <StatusPieChart data={chartDataNPS} innerRadius={22} outerRadius={36} />
        </Box>
      </Box>

      <EmployeesCountListItem
        color={EMPLOYEES_CONFIG_NPS.DETRACTOR_EMPLOYEES.color}
        count={detractorEmployeesCount}
        label={EMPLOYEES_CONFIG_NPS.DETRACTOR_EMPLOYEES.name}
      />
      <EmployeesCountListItem
        color={EMPLOYEES_CONFIG_NPS.PASSIVE_EMPLOYEES.color}
        count={passiveEmployeesCount}
        label={EMPLOYEES_CONFIG_NPS.PASSIVE_EMPLOYEES.name}
      />
      <EmployeesCountListItem
        color={EMPLOYEES_CONFIG_NPS.PROMOTER_EMPLOYEES.color}
        count={promoterEmployeesCount}
        label={EMPLOYEES_CONFIG_NPS.PROMOTER_EMPLOYEES.name}
      />
      <Divider opacity={1} borderColor="#CFD7DF" marginLeft="auto" marginRight="auto" width="95%" />

      <TextLabel padding="4px 0px">{`Score: ${score}`}</TextLabel>
    </Box>
  );
};
